<template>
  <div class="ledger-page pb-5 pb-md-2">
    <b-card class="rounded-lg">
      <div class="d-flex justify-content-between align-items-center flex-wrap mb-1">
        <h4 class="mb-50">
          {{ $t('ledger_periods_title') }}
        </h4>
        <b-button
          v-if="canClose"
          variant="primary"
          class="rounded-pill"
          @click="showCreate = !showCreate"
        >
          {{ $t('ledger_create_period') }}
        </b-button>
      </div>
      <b-row class="mb-1">
        <b-col
          cols="6"
          md="3"
        >
          <b-form-group :label="$t('ledger_year')">
            <b-form-select
              v-model.number="year"
              :options="yearOptions"
              @change="loadPeriods"
            />
          </b-form-group>
        </b-col>
      </b-row>
      <b-row
        v-if="showCreate && canClose"
        class="mb-1"
      >
        <b-col
          cols="6"
          md="3"
        >
          <b-form-input
            v-model.number="createForm.month"
            type="number"
            min="1"
            max="12"
          />
        </b-col>
        <b-col
          cols="6"
          md="3"
        >
          <b-button
            variant="primary"
            :disabled="saving"
            @click="createPeriod"
          >
            {{ $t('ledger_save') }}
          </b-button>
        </b-col>
      </b-row>
      <div
        v-if="loading"
        class="text-center py-3"
      >
        <b-spinner />
      </div>
      <div
        v-else-if="!rows.length"
        class="text-muted"
      >
        {{ $t('ledger_empty_periods') }}
      </div>
      <div v-else>
        <div class="d-none d-md-block">
          <b-table
            :items="sortedRows"
            :fields="fields"
            small
            hover
            responsive
            class="ledger-dense-table"
          >
            <template #cell(period)="data">
              <b-link :to="showRoute(data.item)">
                {{ data.item.period_key || `${data.item.year}-${String(data.item.month).padStart(2, '0')}` }}
              </b-link>
            </template>
            <template #cell(status)="data">
              <b-badge :variant="statusBadgeVariant(enumValue(data.item.status))">
                {{ enumLabel(data.item.status, $i18n.locale) }}
              </b-badge>
              <b-badge
                v-if="data.item.close_forced"
                variant="warning"
                class="ml-50"
              >
                {{ $t('ledger_close_forced') }}
              </b-badge>
            </template>
            <template #cell(closed_by)="data">
              {{ (data.item.closed_by && data.item.closed_by.name) || '—' }}
            </template>
          </b-table>
        </div>
        <div class="d-md-none">
          <article
            v-for="item in sortedRows"
            :key="item.id"
            class="ledger-period-card"
            @click="$router.push(showRoute(item))"
          >
            <div class="d-flex justify-content-between">
              <strong>{{ item.period_key }}</strong>
              <b-badge :variant="statusBadgeVariant(enumValue(item.status))">
                {{ enumLabel(item.status, $i18n.locale) }}
              </b-badge>
            </div>
            <div
              v-if="item.close_forced"
              class="small"
            >
              {{ $t('ledger_close_forced') }}
            </div>
            <div class="text-muted small">
              {{ (item.closed_by && item.closed_by.name) || '' }} {{ item.closed_at || '' }}
            </div>
          </article>
        </div>
      </div>
    </b-card>
  </div>
</template>

<script>
import {
  BCard, BRow, BCol, BFormGroup, BFormSelect, BFormInput, BButton, BTable, BBadge, BLink, BSpinner,
} from 'bootstrap-vue'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { getAccountingPeriods, createAccountingPeriod } from '@/api/accounting/accountingPeriods'
import {
  listFrom, enumValue, enumLabel, statusBadgeVariant, apiErrorMessage,
} from '@/utils/ledger'

export default {
  name: 'AccountingPeriodsList',
  components: {
    BCard, BRow, BCol, BFormGroup, BFormSelect, BFormInput, BButton, BTable, BBadge, BLink, BSpinner,
  },
  data() {
    const now = new Date()
    const year = Number(this.$route.query.year) || now.getFullYear()
    return {
      loading: false,
      saving: false,
      showCreate: false,
      year,
      rows: [],
      createForm: { month: now.getMonth() + 1 },
    }
  },
  computed: {
    canClose() {
      return this.hasPermission('close accounting_periods')
    },
    yearOptions() {
      const current = new Date().getFullYear()
      const years = []
      for (let value = current + 1; value >= current - 6; value -= 1) years.push(value)
      return years
    },
    sortedRows() {
      return [...this.rows].sort((a, b) => b.month - a.month)
    },
    fields() {
      return [
        { key: 'period', label: this.$t('ledger_payroll_period') },
        { key: 'starts_on', label: this.$t('ledger_date') },
        { key: 'ends_on', label: this.$t('ledger_date') },
        { key: 'status', label: this.$t('ledger_status') },
        { key: 'closed_by', label: this.$t('ledger_close_by') },
        { key: 'closed_at', label: this.$t('ledger_close_at') },
      ]
    },
  },
  mounted() {
    this.loadPeriods()
  },
  methods: {
    enumValue,
    enumLabel,
    statusBadgeVariant,
    showRoute(item) {
      return { name: 'ledger-periods-view', params: { id: item.id } }
    },
    toast(title, variant) {
      this.$toast({
        component: ToastificationContent,
        props: { title, icon: variant === 'success' ? 'CheckIcon' : 'AlertCircleIcon', variant },
      })
    },
    async loadPeriods() {
      this.loading = true
      this.$router.replace({ query: { year: this.year } }).catch(() => {})
      try {
        const res = await getAccountingPeriods({ year: this.year })
        this.rows = listFrom(res)
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), 'danger')
      } finally {
        this.loading = false
      }
    },
    async createPeriod() {
      this.saving = true
      try {
        await createAccountingPeriod({ year: Number(this.year), month: Number(this.createForm.month) })
        this.toast(this.$t('ledger_period_created'), 'success')
        this.showCreate = false
        await this.loadPeriods()
      } catch (error) {
        this.toast(apiErrorMessage(error, this.$t('ledger_error')), error?.response?.status === 422 ? 'warning' : 'danger')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.ledger-period-card { border-bottom: 1px solid #eee; padding: 0.85rem 0.15rem; cursor: pointer; }
</style>
